import React, { useState } from 'react';
import { MessageSquare, Send, Search, MoreVertical, Phone, Video, Paperclip, Smile, X, Plus, File, Image as ImageIcon, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import './MessagingCard.css';

const MessagingCard = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showMessageInfoModal, setShowMessageInfoModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [composeData, setComposeData] = useState({
    to: '',
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [scheduleData, setScheduleData] = useState({
    date: '',
    time: ''
  });
  const [attachments, setAttachments] = useState([]);

  const conversations = [
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      lastMessage: 'Thank you for the consultation today. I\'ll follow up next week.',
      time: '2:30 PM',
      unread: 2,
      avatar: 'SJ',
      online: true
    },
    {
      id: 2,
      name: 'Dr. Michael Davis',
      lastMessage: 'The lab results are ready. Please check your email.',
      time: '10:15 AM',
      unread: 0,
      avatar: 'MD',
      online: false
    },
    {
      id: 3,
      name: 'Dr. Emma Wilson',
      lastMessage: 'Can we reschedule tomorrow\'s appointment?',
      time: 'Yesterday',
      unread: 1,
      avatar: 'EW',
      online: true
    },
    {
      id: 4,
      name: 'Dr. David Brown',
      lastMessage: 'The prescription has been sent to your pharmacy.',
      time: 'Dec 12',
      unread: 0,
      avatar: 'DB',
      online: false
    }
  ];

  const messages = [
    {
      id: 1,
      sender: 'Dr. Sarah Johnson',
      message: 'Hello! How are you feeling today?',
      time: '2:30 PM',
      isOwn: false
    },
    {
      id: 2,
      sender: 'You',
      message: 'Hi Dr. Johnson! I\'m feeling much better, thank you.',
      time: '2:32 PM',
      isOwn: true
    },
    {
      id: 3,
      sender: 'Dr. Sarah Johnson',
      message: 'That\'s great to hear! Are you still experiencing any symptoms?',
      time: '2:33 PM',
      isOwn: false
    },
    {
      id: 4,
      sender: 'You',
      message: 'No, the symptoms have completely resolved. The medication worked perfectly.',
      time: '2:35 PM',
      isOwn: true
    },
    {
      id: 5,
      sender: 'Dr. Sarah Johnson',
      message: 'Excellent! I\'m glad the treatment was effective. Let\'s schedule a follow-up in a week to make sure everything stays on track.',
      time: '2:36 PM',
      isOwn: false
    }
  ];

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sendMessage = () => {
    if (message.trim()) {
      // In a real app, this would send the message to the backend
      console.log('Sending message:', message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleComposeMessage = () => {
    setShowComposeModal(true);
  };

  const handleSendCompose = () => {
    console.log('Sending composed message:', composeData);
    alert(`Message sent to ${composeData.to}`);
    setShowComposeModal(false);
    setComposeData({
      to: '',
      subject: '',
      message: '',
      priority: 'normal'
    });
  };

  const handleAttachmentClick = () => {
    setShowAttachmentModal(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, size: f.size, type: f.type }))]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleMessageInfo = (msg) => {
    setSelectedMessage(msg);
    setShowMessageInfoModal(true);
  };

  const handleScheduleMessage = () => {
    setShowScheduleModal(true);
  };

  const handleConfirmSchedule = () => {
    console.log('Scheduling message:', {
      ...scheduleData,
      message: message
    });
    alert(`Message scheduled for ${scheduleData.date} at ${scheduleData.time}`);
    setShowScheduleModal(false);
    setScheduleData({ date: '', time: '' });
    setMessage('');
  };

  return (
    <div className="messaging-container">
      <div className="messaging-sidebar">
        <div className="sidebar-header">
          <h3>Messages</h3>
          <button className="compose-btn" onClick={handleComposeMessage}>
            <Plus size={18} /> Compose
          </button>
        </div>
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="conversations-list">
          {filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`conversation-item ${selectedChat?.id === conversation.id ? 'active' : ''}`}
              onClick={() => setSelectedChat(conversation)}
            >
              <div className="conversation-avatar">
                <span className="avatar-text">{conversation.avatar}</span>
                {conversation.online && <span className="online-indicator"></span>}
              </div>
              <div className="conversation-details">
                <div className="conversation-header">
                  <h4>{conversation.name}</h4>
                  <span className="conversation-time">{conversation.time}</span>
                </div>
                <p className="conversation-preview">{conversation.lastMessage}</p>
                {conversation.unread > 0 && (
                  <span className="unread-badge">{conversation.unread}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="messaging-main">
        {selectedChat ? (
          <>
            <div className="chat-header">
              <div className="chat-info">
                <div className="chat-avatar">
                  <span className="avatar-text">{selectedChat.avatar}</span>
                  {selectedChat.online && <span className="online-indicator"></span>}
                </div>
                <div>
                  <h3>{selectedChat.name}</h3>
                  <span className="chat-status">
                    {selectedChat.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
              <div className="chat-actions">
                <button className="action-btn">
                  <Phone size={16} />
                </button>
                <button className="action-btn">
                  <Video size={16} />
                </button>
                <button className="action-btn">
                  <MoreVertical size={16} />
                </button>
              </div>
            </div>

            <div className="messages-container">
              <div className="messages-list">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.isOwn ? 'own' : 'other'}`}
                  >
                    <div className="message-content">
                      <p>{msg.message}</p>
                      <span className="message-time">{msg.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="message-input-container">
              <div className="message-input-wrapper">
                <button className="input-action-btn" onClick={handleAttachmentClick} title="Attach files">
                  <Paperclip size={16} />
                </button>
                <button className="input-action-btn" onClick={handleScheduleMessage} title="Schedule message">
                  <Clock size={16} />
                </button>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="message-input"
                  rows={1}
                />
                <button className="input-action-btn">
                  <Smile size={16} />
                </button>
                <button 
                  className="send-btn"
                  onClick={sendMessage}
                  disabled={!message.trim()}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-chat-selected">
            <MessageSquare size={48} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar to start messaging</p>
          </div>
        )}
      </div>

      {/* Compose Message Modal */}
      {showComposeModal && (
        <div className="msg-modal-overlay" onClick={() => setShowComposeModal(false)}>
          <div className="msg-modal msg-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="msg-modal-header">
              <h2>Compose New Message</h2>
              <button className="msg-modal-close" onClick={() => setShowComposeModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="msg-modal-body">
              <div className="msg-form-group">
                <label>To</label>
                <input 
                  type="text"
                  placeholder="Enter recipient name or email"
                  value={composeData.to}
                  onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                />
              </div>

              <div className="msg-form-group">
                <label>Subject</label>
                <input 
                  type="text"
                  placeholder="Message subject"
                  value={composeData.subject}
                  onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>

              <div className="msg-form-group">
                <label>Priority</label>
                <select 
                  value={composeData.priority}
                  onChange={(e) => setComposeData(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="msg-form-group">
                <label>Message</label>
                <textarea 
                  rows="10"
                  placeholder="Type your message here..."
                  value={composeData.message}
                  onChange={(e) => setComposeData(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>

              {attachments.length > 0 && (
                <div className="attachments-preview">
                  <h4>Attachments ({attachments.length})</h4>
                  {attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      <File size={16} />
                      <span>{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                      <button onClick={() => removeAttachment(index)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="msg-modal-footer">
              <button className="msg-btn-outline" onClick={() => setShowComposeModal(false)}>Cancel</button>
              <button className="msg-btn-outline" onClick={handleAttachmentClick}>
                <Paperclip size={16} /> Attach Files
              </button>
              <button className="msg-btn-primary" onClick={handleSendCompose} disabled={!composeData.to || !composeData.message}>
                <Send size={16} /> Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Modal */}
      {showAttachmentModal && (
        <div className="msg-modal-overlay" onClick={() => setShowAttachmentModal(false)}>
          <div className="msg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="msg-modal-header">
              <h2>Attach Files</h2>
              <button className="msg-modal-close" onClick={() => setShowAttachmentModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="msg-modal-body">
              <div className="attachment-upload-area">
                <input 
                  type="file" 
                  id="file-upload" 
                  multiple 
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="upload-label">
                  <Paperclip size={32} />
                  <p>Click to browse or drag and drop files here</p>
                  <span>Supported formats: PDF, DOC, JPEG, PNG (Max 10MB each)</span>
                </label>
              </div>

              {attachments.length > 0 && (
                <div className="attachments-list">
                  <h4>Selected Files ({attachments.length})</h4>
                  {attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      {file.type?.includes('image') ? <ImageIcon size={18} /> : <File size={18} />}
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                      </div>
                      <button className="remove-btn" onClick={() => removeAttachment(index)}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="msg-modal-footer">
              <button className="msg-btn-outline" onClick={() => setShowAttachmentModal(false)}>Close</button>
              <button className="msg-btn-primary" onClick={() => setShowAttachmentModal(false)}>
                <CheckCircle2 size={16} /> Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Message Modal */}
      {showScheduleModal && (
        <div className="msg-modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="msg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="msg-modal-header">
              <h2>Schedule Message</h2>
              <button className="msg-modal-close" onClick={() => setShowScheduleModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="msg-modal-body">
              <div className="schedule-info">
                <AlertCircle size={20} color="#1f73e6" />
                <p>Choose when you want this message to be sent</p>
              </div>

              <div className="msg-form-row">
                <div className="msg-form-group">
                  <label>Date</label>
                  <input 
                    type="date"
                    value={scheduleData.date}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="msg-form-group">
                  <label>Time</label>
                  <input 
                    type="time"
                    value={scheduleData.time}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, time: e.target.value }))}
                  />
                </div>
              </div>

              {scheduleData.date && scheduleData.time && (
                <div className="schedule-preview">
                  <Clock size={18} />
                  <p>This message will be sent on <strong>{scheduleData.date}</strong> at <strong>{scheduleData.time}</strong></p>
                </div>
              )}

              <div className="msg-form-group">
                <label>Quick Schedule</label>
                <div className="quick-schedule-btns">
                  <button onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setScheduleData(prev => ({ 
                      ...prev, 
                      date: tomorrow.toISOString().split('T')[0],
                      time: '09:00'
                    }));
                  }}>Tomorrow 9 AM</button>
                  <button onClick={() => {
                    const nextWeek = new Date();
                    nextWeek.setDate(nextWeek.getDate() + 7);
                    setScheduleData(prev => ({ 
                      ...prev, 
                      date: nextWeek.toISOString().split('T')[0],
                      time: '09:00'
                    }));
                  }}>Next Week</button>
                  <button onClick={() => {
                    const today = new Date();
                    setScheduleData(prev => ({ 
                      ...prev, 
                      date: today.toISOString().split('T')[0],
                      time: '17:00'
                    }));
                  }}>Today 5 PM</button>
                </div>
              </div>
            </div>
            <div className="msg-modal-footer">
              <button className="msg-btn-outline" onClick={() => setShowScheduleModal(false)}>Cancel</button>
              <button 
                className="msg-btn-primary" 
                onClick={handleConfirmSchedule}
                disabled={!scheduleData.date || !scheduleData.time}
              >
                <CheckCircle2 size={16} /> Schedule Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagingCard;
